// Idea API Hooks - React Query hooks for idea operations
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys, invalidateQueries } from '../../lib/query-client';
import apiClient from '../../services/unifiedApiClient';

// Fetch all ideas for current user (handles pagination automatically)
export const useIdeas = (options = {}) => {
  return useQuery({
    queryKey: queryKeys.ideas.list({}),
    queryFn: async () => {
      const pageSize = 100;
      let allIdeas = [];
      let offset = 0;
      let total = 0;

      // Fetch first page to get total count
      const firstResponse = await apiClient.get('/ideas', { params: { limit: pageSize, offset: 0 } });

      if (firstResponse && firstResponse.ideas) {
        allIdeas = [...firstResponse.ideas];
        total = firstResponse.total || firstResponse.ideas.length;
      } else if (Array.isArray(firstResponse)) {
        return { items: firstResponse, total: firstResponse.length };
      } else {
        return { items: [], total: 0 };
      }

      // Fetch remaining pages if needed
      while (allIdeas.length < total) {
        offset += pageSize;
        const response = await apiClient.get('/ideas', { params: { limit: pageSize, offset } });
        if (response && response.ideas && response.ideas.length > 0) {
          allIdeas = [...allIdeas, ...response.ideas];
        } else {
          break; // No more ideas
        }
      }

      return { items: allIdeas, total };
    },
    ...options,
  });
};

// Fetch single idea by ID
export const useIdea = (ideaId, options = {}) => {
  return useQuery({
    queryKey: queryKeys.ideas.detail(ideaId),
    queryFn: async () => {
      if (!ideaId) return null;
      return await apiClient.get(`/ideas/${ideaId}`);
    },
    enabled: !!ideaId,
    ...options,
  });
};

// Fetch idea progress (for polling during generation)
export const useIdeaProgress = (ideaId, options = {}) => {
  return useQuery({
    queryKey: queryKeys.ideas.progress(ideaId),
    queryFn: async () => {
      if (!ideaId) return null;
      return await apiClient.get(`/ideas/${ideaId}/progress`);
    },
    enabled: !!ideaId,
    refetchInterval: options.polling ? 2000 : false, // Poll every 2 seconds if enabled
    ...options,
  });
};

// Fetch plans for an idea
export const useIdeaPlans = (ideaId, options = {}) => {
  return useQuery({
    queryKey: queryKeys.ideas.plans(ideaId),
    queryFn: async () => {
      if (!ideaId) return { items: [], total: 0 };
      const response = await apiClient.get(`/ideas/${ideaId}/plans`);

      if (response && response.plans) {
        return {
          items: response.plans,
          total: response.total || response.plans.length,
        };
      }

      if (Array.isArray(response)) {
        return {
          items: response,
          total: response.length,
        };
      }

      return { items: [], total: 0 };
    },
    enabled: !!ideaId,
    ...options,
  });
};

// Create idea mutation
export const useCreateIdea = () => {
  return useMutation({
    mutationFn: async (ideaData) => {
      return await apiClient.post('/ideas', ideaData);
    },
    onSuccess: () => {
      invalidateQueries.ideas.list();
    },
    onError: (error) => {
      console.error('Failed to create idea:', error);
    },
  });
};

// Update idea mutation
export const useUpdateIdea = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ideaId, updates }) => {
      return await apiClient.patch(`/ideas/${ideaId}`, updates);
    },
    onSuccess: (data, { ideaId }) => {
      queryClient.setQueryData(queryKeys.ideas.detail(ideaId), data);
      invalidateQueries.ideas.list();
    },
    onError: (error) => {
      console.error('Failed to update idea:', error);
    },
  });
};

// Delete idea mutation
export const useDeleteIdea = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ideaId) => {
      return await apiClient.delete(`/ideas/${ideaId}`);
    },
    onMutate: async (ideaId) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.ideas.lists() });

      // Snapshot all cached idea list queries for rollback
      const snapshots = queryClient.getQueriesData({ queryKey: queryKeys.ideas.lists() });

      // Optimistically remove from all cached list queries (handles any filter variations)
      queryClient.setQueriesData({ queryKey: queryKeys.ideas.lists() }, (old) => {
        if (!old) return old;
        return {
          ...old,
          items: (old.items || []).filter(p => p.id !== ideaId),
          total: Math.max(0, (old.total || 0) - 1),
        };
      });

      return { snapshots };
    },
    onError: (error, ideaId, context) => {
      console.error('Failed to delete idea:', error);
      if (context?.snapshots) {
        for (const [queryKey, data] of context.snapshots) {
          queryClient.setQueryData(queryKey, data);
        }
      }
    },
    onSettled: (data, error, ideaId) => {
      queryClient.removeQueries({ queryKey: queryKeys.ideas.detail(ideaId) });
      invalidateQueries.ideas.list();
    },
  });
};

// Delete plan mutation
export const useDeletePlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ planId }) => {
      return await apiClient.delete(`/plans/${planId}`);
    },
    onSuccess: (data, { planId, ideaId }) => {
      // Invalidate the idea's plans list
      if (ideaId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.ideas.plans(ideaId) });
      }
      // Invalidate general plans queries
      queryClient.invalidateQueries({ queryKey: queryKeys.plans.all });
    },
    onError: (error) => {
      console.error('Failed to delete plan:', error);
    },
  });
};


// ============================================================================
// Discovery hooks
// ============================================================================

// Generate playback mutation
export const useGeneratePlayback = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ideaId) => {
      return await apiClient.post(`/ideas/${ideaId}/discovery/playback`);
    },
    onSuccess: (data, ideaId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ideas.detail(ideaId) });
    },
  });
};

// Confirm playback mutation
export const useConfirmPlayback = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ideaId, playback }) => {
      return await apiClient.put(`/ideas/${ideaId}/discovery/playback`, { playback });
    },
    onSuccess: (data, { ideaId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ideas.detail(ideaId) });
    },
  });
};

// Generate assumptions mutation
export const useGenerateAssumptions = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ideaId) => {
      return await apiClient.post(`/ideas/${ideaId}/discovery/assumptions`);
    },
    onSuccess: (data, ideaId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ideas.detail(ideaId) });
    },
  });
};

// Confirm assumptions mutation
export const useConfirmAssumptions = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ideaId, assumptions }) => {
      return await apiClient.put(`/ideas/${ideaId}/discovery/assumptions`, { assumptions });
    },
    onSuccess: (data, { ideaId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ideas.detail(ideaId) });
    },
  });
};

// Generate risks mutation
export const useGenerateRisks = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ideaId) => {
      return await apiClient.post(`/ideas/${ideaId}/discovery/risks`);
    },
    onSuccess: (data, ideaId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ideas.detail(ideaId) });
    },
  });
};

// Acknowledge risks mutation
export const useAcknowledgeRisks = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ideaId, risks }) => {
      return await apiClient.put(`/ideas/${ideaId}/discovery/risks`, { risks });
    },
    onSuccess: (data, { ideaId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ideas.detail(ideaId) });
    },
  });
};

// Update risks post-discovery (no phase change — for Considerations tab editing)
export const useUpdateRisks = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ideaId, risks }) => {
      return await apiClient.patch(`/ideas/${ideaId}/discovery/risks`, { risks });
    },
    onSuccess: (data, { ideaId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ideas.detail(ideaId) });
    },
  });
};

// Retry last failed background operation
export const useRetryIdea = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ideaId) => {
      return await apiClient.post(`/ideas/${ideaId}/retry`);
    },
    onSuccess: (data, ideaId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ideas.detail(ideaId) });
    },
  });
};

// Submit discovery scoping (triggers plan creation)
export const useSubmitDiscovery = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ideaId, phases, features, existing_features }) => {
      return await apiClient.post(`/ideas/${ideaId}/discovery/submit`, { phases, features, existing_features });
    },
    onSuccess: (data, { ideaId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ideas.detail(ideaId) });
      invalidateQueries.ideas.list();
    },
  });
};

// Update discovery questions mutation (add/remove/reorder)
export const useUpdateDiscoveryQuestions = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ideaId, questions }) => {
      return await apiClient.put(`/ideas/${ideaId}/discovery/questions`, { questions });
    },
    onSuccess: (data, { ideaId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ideas.detail(ideaId) });
      invalidateQueries.ideas.list();
    },
    onError: (error) => {
      console.error('Failed to update discovery questions:', error);
    },
  });
};

// Update idea features mutation (add/remove/edit)
export const useUpdateIdeaFeatures = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ideaId, features }) => {
      return await apiClient.put(`/ideas/${ideaId}/spec/features`, { features });
    },
    onSuccess: (data, { ideaId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ideas.detail(ideaId) });
      invalidateQueries.ideas.list();
    },
    onError: (error) => {
      console.error('Failed to update idea features:', error);
    },
  });
};

// Generate idea spec mutation
export const useGenerateSpec = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (ideaId) => {
      return await apiClient.post(`/ideas/${ideaId}/spec`);
    },
    onSuccess: (data, ideaId) => {
      queryClient.setQueryData(queryKeys.ideas.detail(ideaId), data);
      invalidateQueries.ideas.list();
    },
    onError: (error) => {
      console.error('Failed to generate spec:', error);
    },
  });
};

// Regenerate spec with feedback mutation
export const useRegenerateSpec = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ ideaId, feedback }) => {
      return await apiClient.post(`/ideas/${ideaId}/spec/feedback`, { feedback });
    },
    onSuccess: (data, { ideaId }) => {
      queryClient.setQueryData(queryKeys.ideas.detail(ideaId), data);
      invalidateQueries.ideas.list();
    },
    onError: (error) => {
      console.error('Failed to regenerate spec:', error);
    },
  });
};

// Create plan from idea mutation
export const useCreatePlanFromIdea = () => {
  return useMutation({
    mutationFn: async ({ ideaId, planData }) => {
      return await apiClient.post(`/ideas/${ideaId}/plans`, planData);
    },
    onSuccess: (data, { ideaId }) => {
      invalidateQueries.ideas.plans(ideaId);
      invalidateQueries.plans.all();
      invalidateQueries.tasks.list();
    },
    onError: (error) => {
      console.error('Failed to create plan:', error);
    },
  });
};

// Export idea (with all plans) mutation
export const useExportIdea = () => {
  return useMutation({
    mutationFn: async ({ ideaId, format = 'json', includePlans = true, includeSpec = true, includeAssumptions = true, featureName = null, planName = null }) => {
      return await apiClient.post(`/ideas/${ideaId}/export`, {
        format,
        include_projects: includePlans,
        include_spec: includeSpec,
        include_assumptions: includeAssumptions,
        ...(featureName ? { feature_name: featureName } : {}),
        ...(planName ? { plan_name: planName } : {}),
      });
    },
    onError: (error) => {
      console.error('Failed to export idea:', error);
    },
  });
};

// ============================================================================
// Repo Management Hooks
// ============================================================================

// Fetch connected repos for an idea
export const useIdeaRepos = (ideaId, options = {}) => {
  return useQuery({
    queryKey: [...queryKeys.ideas.detail(ideaId), 'repos'],
    queryFn: async () => {
      if (!ideaId) return [];
      return await apiClient.get(`/ideas/${ideaId}/repos`);
    },
    enabled: !!ideaId,
    ...options,
  });
};

// Add a repo to an idea
export const useAddRepo = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ideaId, url, defaultBranch = 'main' }) => {
      return await apiClient.post(`/ideas/${ideaId}/repos`, {
        url,
        default_branch: defaultBranch,
      });
    },
    onSuccess: (data, { ideaId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ideas.detail(ideaId) });
    },
  });
};

// Remove a repo from an idea
export const useRemoveRepo = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ideaId, url }) => {
      return await apiClient.delete(`/ideas/${ideaId}/repos`, {
        data: { url },
      });
    },
    onSuccess: (data, { ideaId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ideas.detail(ideaId) });
    },
  });
};

// ============================================================================
// Drift State Hooks
// ============================================================================

// Fetch persisted drift state (findings-based shape)
// Returns: { findings, previous_findings, repos: {...}, github_repos }
export const useDriftState = (ideaId, options = {}) => {
  return useQuery({
    queryKey: [...queryKeys.ideas.detail(ideaId), 'drift'],
    queryFn: async () => {
      if (!ideaId) return null;
      return await apiClient.get(`/ideas/${ideaId}/drift/state`);
    },
    enabled: !!ideaId,
    ...options,
  });
};

// Trigger a drift scan. Optional `repo` ("owner/name") scopes to one repo.
export const useTriggerDriftScan = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ideaId, repo }) => {
      const body = repo ? { repo } : {};
      return await apiClient.post(`/ideas/${ideaId}/drift/detect`, body, {
        timeout: 180000, // 3 min — multi-repo scans can be slow
      });
    },
    onSuccess: (data, { ideaId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ideas.detail(ideaId) });
      queryClient.invalidateQueries({ queryKey: [...queryKeys.ideas.detail(ideaId), 'drift'] });
    },
  });
};

// Preview what applying a finding would do — both the spec edits and the
// LLM-suggested downstream cascades. NO mutation. Used to show a confirmation
// dialog with cascade checkboxes before the user commits.
export const usePreviewUpdateSpec = () => {
  return useMutation({
    mutationFn: async ({ ideaId, findingId }) => {
      return await apiClient.post(
        `/ideas/${ideaId}/drift/findings/${findingId}/preview-update-spec`
      );
    },
  });
};

// Apply a finding's proposed_spec_diff plus a set of approved cascades.
// Caller passes the cascades the user approved in the preview modal.
export const useUpdateSpecFromFinding = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ideaId, findingId, approvedCascades = [] }) => {
      return await apiClient.post(
        `/ideas/${ideaId}/drift/findings/${findingId}/update-spec`,
        { approved_cascades: approvedCascades }
      );
    },
    onSuccess: (data, { ideaId }) => {
      // Refresh the idea (spec was bumped, plans may have changed) but leave the
      // drift cache alone — the caller filters applied findings locally.
      queryClient.invalidateQueries({ queryKey: queryKeys.ideas.detail(ideaId) });
    },
  });
};

// Preview what changes would result from renaming an idea — section-level
// spec edits (executive_summary, problem_statement, etc.) plus plan-level
// cascades for stories, features, and event-model elements that mention the
// old name. Same response shape as the drift Update spec preview, so the
// confirmation modal renders it directly.
export const usePreviewIdeaRename = (ideaId) => {
  return useMutation({
    mutationFn: async ({ newName }) => {
      return await apiClient.post(`/ideas/${ideaId}/rename/preview`, { new_name: newName });
    },
  });
};

// Apply a rename — sets idea.name and applies approved cascades + spec edits.
export const useApplyIdeaRename = (ideaId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ newName, approvedCascades, confirmedSpecEdits }) => {
      return await apiClient.post(`/ideas/${ideaId}/rename`, {
        new_name: newName,
        approved_cascades: approvedCascades || [],
        confirmed_spec_edits: confirmedSpecEdits || [],
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.ideas.detail(ideaId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.ideas.lists() });
    },
  });
};

// Mark a drift finding as not-applicable. Persists across re-scans (the
// signature of the finding — repo + file_path + normalized title — is stored
// server-side so re-detected findings stay hidden).
export const useDismissFinding = (ideaId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ findingId }) => {
      return await apiClient.post(`/ideas/${ideaId}/drift/findings/${findingId}/dismiss`);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        [...queryKeys.ideas.detail(ideaId), 'drift'],
        data,
      );
    },
  });
};

// Restore a previously dismissed drift finding by signature.
export const useUndismissFinding = (ideaId) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ signature }) => {
      return await apiClient.post(`/ideas/${ideaId}/drift/findings/undismiss`, { signature });
    },
    onSuccess: (data) => {
      queryClient.setQueryData(
        [...queryKeys.ideas.detail(ideaId), 'drift'],
        data,
      );
    },
  });
};

// Fetch recent commits across one or more connected repos, merged and sorted.
export const useRecentCommits = (repos, { limit = 20 } = {}) => {
  // repos: array of { owner, repo, default_branch }
  const repoKey = JSON.stringify(repos || []);
  return useQuery({
    queryKey: ['github', 'commits', repoKey, limit],
    queryFn: async () => {
      if (!repos || repos.length === 0) return [];
      const all = await Promise.all(repos.map(async (r) => {
        const data = await apiClient.get('/github/commits', {
          params: { owner: r.owner, repo: r.repo, ref: r.default_branch || 'main', limit },
        });
        return (data?.commits || []).map(c => ({ ...c, repo: `${r.owner}/${r.repo}` }));
      }));
      return all.flat()
        .sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''))
        .slice(0, limit);
    },
    enabled: !!repos && repos.length > 0,
  });
};

// Trigger engineering decomposition for a plan
export const useGeneratePlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ planId }) => {
      return await apiClient.post(`/plans/${planId}/generate`);
    },
    onSuccess: (data, { ideaId }) => {
      if (ideaId) queryClient.invalidateQueries({ queryKey: queryKeys.ideas.plans(ideaId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.plans.all });
    },
    onError: (error) => {
      console.error('Failed to generate plan:', error);
    },
  });
};

// Update plan scope (for post-drift adjustments)
export const useUpdatePlanScope = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ planId, scope, scopeDescription, selectedFeatures, excludedFeatures }) => {
      return await apiClient.patch(`/plans/${planId}/scope`, {
        scope,
        scope_description: scopeDescription,
        selected_features: selectedFeatures,
        excluded_features: excludedFeatures,
      });
    },
    onSuccess: (data, { planId }) => {
      // Invalidate plan queries
      queryClient.invalidateQueries({ queryKey: queryKeys.plans.all });
    },
    onError: (error) => {
      console.error('Failed to update plan scope:', error);
    },
  });
};

// Update plan lifecycle status
export const useUpdatePlanLifecycle = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ planId, lifecycleStatus }) => {
      return await apiClient.patch(`/plans/${planId}/lifecycle`, {
        lifecycle_status: lifecycleStatus,
      });
    },
    onSuccess: (data, { ideaId }) => {
      if (ideaId) queryClient.invalidateQueries({ queryKey: queryKeys.ideas.plans(ideaId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.plans.all });
    },
    onError: (error) => {
      console.error('Failed to update plan lifecycle:', error);
    },
  });
};

